import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchAllAktienUebersichtApi,
  fetchAktieDetailApi,
  fetchAktieLiveApi,
  fetchAktieAnalyseApi,
} from './aktienApi';

/**
 * Redux-Slice fuer die in der DATENBANK gespeicherten Aktien ("Analyse").
 *
 * - `liste`        : Uebersicht aller DB-Aktien (Name + letzter Kurs).
 * - `suchbegriff`  : aktueller Filtertext der Liste; bleibt erhalten, damit man
 *                    aus der Detailansicht zur vorherigen Suche zurueckkehren kann.
 * - `ausgewaehlteIsin` : zuletzt angeklickte Aktie (fuer den Reiter "Aktie Details").
 * - `detail`       : Stammdaten + Tageswerte/Jahreswerte der ausgewaehlten Aktie.
 *
 * @typedef {import('types/dto').AktieUebersichtDTO} AktieUebersichtDTO
 * @typedef {import('types/dto').AktieDTO} AktieDTO
 */

export const fetchAllAktienUebersicht = createAsyncThunk(
  'aktienDb/fetchAlle',
  async () => {
    return (await fetchAllAktienUebersichtApi()) ?? [];
  }
);

export const fetchAktieDetail = createAsyncThunk(
  'aktienDb/fetchDetail',
  async (isin) => {
    return await fetchAktieDetailApi(isin);
  }
);

// Aktueller Live-Kurs (separat, weil der OnVista-Abruf 1-3s dauern kann und das
// hervorgehobene Kursfeld nicht das Laden der Stammdaten/Tabellen blockieren soll).
export const fetchAktieLive = createAsyncThunk(
  'aktienDb/fetchLive',
  async (isin) => {
    return await fetchAktieLiveApi(isin);
  }
);

// Berechnete Analyse der ausgewaehlten Aktie (Chart/Fundamental/Dividende/Risiko).
// Wird beim Auswaehlen einer Aktie automatisch neu geladen.
export const fetchAktieAnalyse = createAsyncThunk(
  'aktienDb/fetchAnalyse',
  async (isin) => {
    return await fetchAktieAnalyseApi(isin);
  }
);

const initialState = {
  liste: [],
  listeStatus: 'idle', // idle | loading | succeeded | failed
  suchbegriff: '',
  ausgewaehlteIsin: null,
  detail: null,
  detailStatus: 'idle',
  live: null, // aktueller Live-Kurs (Tageswert) der ausgewählten Aktie
  liveStatus: 'idle',
  analyse: null, // berechnete Analyse der ausgewählten Aktie
  analyseStatus: 'idle',
  error: null,
};

const aktienDbSlice = createSlice({
  name: 'aktienDb',
  initialState,
  reducers: {
    setSuchbegriff(state, action) {
      state.suchbegriff = action.payload;
    },
    selectAktie(state, action) {
      state.ausgewaehlteIsin = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllAktienUebersicht.pending, (state) => {
        state.listeStatus = 'loading';
        state.error = null;
      })
      .addCase(fetchAllAktienUebersicht.fulfilled, (state, action) => {
        state.listeStatus = 'succeeded';
        state.liste = action.payload;
      })
      .addCase(fetchAllAktienUebersicht.rejected, (state, action) => {
        state.listeStatus = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchAktieDetail.pending, (state) => {
        state.detailStatus = 'loading';
        state.detail = null;
        state.error = null;
      })
      .addCase(fetchAktieDetail.fulfilled, (state, action) => {
        state.detailStatus = 'succeeded';
        state.detail = action.payload;
      })
      .addCase(fetchAktieDetail.rejected, (state, action) => {
        state.detailStatus = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchAktieLive.pending, (state) => {
        state.liveStatus = 'loading';
        state.live = null;
      })
      .addCase(fetchAktieLive.fulfilled, (state, action) => {
        state.liveStatus = 'succeeded';
        state.live = action.payload;
      })
      .addCase(fetchAktieLive.rejected, (state) => {
        // Fehlschlag ist unkritisch: das Kursfeld fällt auf den gespeicherten Wert zurück.
        state.liveStatus = 'failed';
        state.live = null;
      })
      .addCase(fetchAktieAnalyse.pending, (state) => {
        state.analyseStatus = 'loading';
        state.analyse = null;
        state.error = null;
      })
      .addCase(fetchAktieAnalyse.fulfilled, (state, action) => {
        state.analyseStatus = 'succeeded';
        state.analyse = action.payload;
      })
      .addCase(fetchAktieAnalyse.rejected, (state, action) => {
        state.analyseStatus = 'failed';
        state.error = action.error.message;
      });
  },
});

export const { setSuchbegriff, selectAktie } = aktienDbSlice.actions;

export const selectAktienListe = (state) => state.aktienDb.liste;
export const selectAktienListeStatus = (state) => state.aktienDb.listeStatus;
export const selectSuchbegriff = (state) => state.aktienDb.suchbegriff;
export const selectAusgewaehlteIsin = (state) => state.aktienDb.ausgewaehlteIsin;
export const selectAktieDetail = (state) => state.aktienDb.detail;
export const selectAktieDetailStatus = (state) => state.aktienDb.detailStatus;
export const selectAktieLive = (state) => state.aktienDb.live;
export const selectAktieLiveStatus = (state) => state.aktienDb.liveStatus;
export const selectAktieAnalyse = (state) => state.aktienDb.analyse;
export const selectAktieAnalyseStatus = (state) => state.aktienDb.analyseStatus;

export default aktienDbSlice.reducer;
